import { Injectable } from '@angular/core';
import { APP_CONFIG } from '../../environment';
import {
  IBankDepositBankSearchRequestEntity,
  IBankDepositBankSearchResponseEntity,
  IBankDepositIsSmartRoutingEnabledRequestEntity,
  IBankDepositIsSmartRoutingEnabledResponseEntity,
  IBankDepositRealTimeValidationRequestEntity,
  IBankDepositRealTimeValidationResponseEntity,
  IBankDepositTransactionRequirementsRequestEntity,
  IBankDepositTransactionRequirementsResponseEntity
} from '../../model/api/bank-deposit';
import { IBankDepositSmartRoutingProvider } from '../../model/models/agent/bank-deposit-provider.interface';
import { IOrder } from '../../model/models/order/order-interface';
import { AccountFormat, EntitySetting } from '../../model/models/agent/wallet.interface';
import { SmartRoutingFeatureType } from '../../model/types/smart-routing-feature-type';
import { HttpProvider } from '../http/http';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { FeatureFlagTypes, UtilsProvider } from '../myria/utils-provider';

/** Smart Routing for bank-deposit payouts, against the CST BankDeposit endpoints. */
@Injectable()
export class BankDepositSmartRoutingProvider {
  /** Corridor gate. Decides which endpoints to call, never which ids go on the wire. */
  useBankDepositSmartRouting: boolean;
  /** Whether the latest transactionRequirements response really was smart-routed. */
  bankDepositSmartRoutingApplied: boolean;
  bankDepositProvider: IBankDepositSmartRoutingProvider | null;
  snapshotToken: string | null;
  entitySettings: EntitySetting[];
  accountFormats: AccountFormat[];
  accountFormatLabel: string | null;
  private supportedFeatures: string[] = [];
  /** The corridor the gate was last resolved for, so a gate resolved elsewhere cannot leak into another corridor. */
  private resolvedCorridor: IBankDepositIsSmartRoutingEnabledRequestEntity = null;
  private resolvedCorridorKey: string = null;

  constructor(private httpProvider: HttpProvider, private utilsProvider: UtilsProvider) {
  }

  /** The only thing outbound wire fields may read. */
  get isSmartRoutedTransaction(): boolean {
    return !!this.useBankDepositSmartRouting
      && !!this.bankDepositSmartRoutingApplied
      && !!this.bankDepositProvider;
  }

  get supportsRealTimeValidation(): boolean {
    return !!this.entitySettings && this.entitySettings.some(setting => setting.RealTimeValidation);
  }

  get advertisedFeatures(): string[] {
    return this.supportedFeatures ? this.supportedFeatures.slice() : [];
  }

  supportsFeature(feature: SmartRoutingFeatureType): boolean {
    return !!this.useBankDepositSmartRouting
      && !!this.supportedFeatures
      && this.supportedFeatures.indexOf(feature) >= 0;
  }

  /** The corridor fields banksearch qualifies banks on, taken from the gate the corridor was resolved with. */
  get searchCorridor(): IBankDepositIsSmartRoutingEnabledRequestEntity | null {
    return this.resolvedCorridor;
  }

  /** Resolves the gate at most once per corridor. Every consumer goes through this, not through isSmartRoutingEnabled. */
  async ensureSmartRoutingEnabled(params: IBankDepositIsSmartRoutingEnabledRequestEntity): Promise<boolean> {
    if (this.resolvedCorridorKey !== null && this.resolvedCorridorKey === this.corridorKey(params))
      return !!this.useBankDepositSmartRouting;
    return this.isSmartRoutingEnabled(params);
  }

  /**
   * The corridor of an order, for the callers that hold one. Every entry point that can build a bank
   * deposit resolves the gate through here, so none of them is left on the legacy engine by omission.
   */
  async ensureSmartRoutingEnabledForOrder(order: IOrder): Promise<boolean> {
    if (!order)
      return false;
    return this.ensureSmartRoutingEnabled({
      countryTo: order.countryTo,
      currencyFrom: order.currencyFrom,
      currencyTo: order.currencyTo,
      amountFrom: order.amountFrom,
      amountTo: order.amountTo
    });
  }

  /**
   * Resolves the provider without rendering a form, for a transfer whose bank and account format are
   * already known. Null when the response came back from the legacy engine, and null on failure too:
   * callers fail the transfer on a missing provider rather than falling back to the legacy engine.
   */
  async resolveProvider(params: IBankDepositTransactionRequirementsRequestEntity): Promise<IBankDepositSmartRoutingProvider | null> {
    try {
      await this.getTransactionRequirements(params);
    } catch (ex) {
      this.clearResolvedProvider();
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to resolve the bank deposit smart routing provider');
      return null;
    }
    return this.isSmartRoutedTransaction ? this.bankDepositProvider : null;
  }

  /** True only while the gate is on for this destination country, so a stale gate cannot re-route another corridor. */
  isEnabledForCountry(countryTo: string): boolean {
    return !!this.useBankDepositSmartRouting
      && !!countryTo
      && !!this.resolvedCorridor
      && this.resolvedCorridor.countryTo === countryTo;
  }

  isEnabledForCorridor(params: IBankDepositIsSmartRoutingEnabledRequestEntity): boolean {
    return !!this.useBankDepositSmartRouting
      && this.resolvedCorridorKey !== null
      && this.resolvedCorridorKey === this.corridorKey(params);
  }

  /** Corridor gate — POST api/bankdeposit/IsSmartRoutingEnabled. Fails closed on every path. */
  async isSmartRoutingEnabled(params: IBankDepositIsSmartRoutingEnabledRequestEntity): Promise<boolean> {
    this.useBankDepositSmartRouting = false;
    this.supportedFeatures = [];
    this.resolvedCorridor = params || null;
    this.resolvedCorridorKey = this.corridorKey(params);
    this.clearResolvedProvider();
    try {
      // getFeatureFlagData waits for the flags and parses the value, where the raw string 'false' would be truthy.
      const featureFlag = await this.utilsProvider.getFeatureFlagData(FeatureFlagTypes.UsingBankDepositSmartRouting);
      if (featureFlag !== true) {
        LoggerProvider.info(this, 'Bank deposit smart routing feature flag is disabled');
        return false;
      }
      if (!params || !params.countryTo || !params.currencyFrom || !params.currencyTo) {
        LoggerProvider.info(this, 'Bank deposit smart routing gate skipped, incomplete corridor data');
        return false;
      }
      const res: IBankDepositIsSmartRoutingEnabledResponseEntity =
        await this.httpProvider.post(`${APP_CONFIG.API_URL}api/bankdeposit/IsSmartRoutingEnabled`, params);
      if (!res || !res.IsEnabled || res.UseFallback) {
        LoggerProvider.info(this, 'Bank deposit smart routing not enabled for corridor', res);
        return false;
      }
      this.useBankDepositSmartRouting = true;
      this.supportedFeatures = res.SupportedFeatures || [];
      LoggerProvider.info(this, 'Bank deposit smart routing enabled for corridor', this.supportedFeatures);
      return true;
    } catch (ex) {
      this.useBankDepositSmartRouting = false;
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to resolve bank deposit smart routing corridor gate');
      return false;
    }
  }

  /** POST api/bankdeposit/banksearch. Gates on IsSuccess + EntityList; `page` is 1-indexed. */
  async searchBanks(params: IBankDepositBankSearchRequestEntity): Promise<IBankDepositBankSearchResponseEntity> {
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/bankdeposit/banksearch`, params);
  }

  /** POST api/bankdeposit/transactionRequirements. The single place the provider triple is resolved. */
  async getTransactionRequirements(params: IBankDepositTransactionRequirementsRequestEntity): Promise<IBankDepositTransactionRequirementsResponseEntity> {
    this.clearResolvedProvider();
    const res: IBankDepositTransactionRequirementsResponseEntity =
      await this.httpProvider.post(`${APP_CONFIG.API_URL}api/bankdeposit/transactionRequirements`, { ...params, useSmartRouting: true });
    this.recordTransactionRequirements(res, params.bankId);
    return res;
  }

  /** POST api/bankdeposit/realTimeValidation. `providerId` is the correspondent, not the routing id. */
  async validateRealTime(params: IBankDepositRealTimeValidationRequestEntity): Promise<IBankDepositRealTimeValidationResponseEntity> {
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/bankdeposit/realTimeValidation`, params);
  }

  /** Null unless the account was rejected; fails open. */
  async findAccountRejection(params: {
    countryTo: string,
    bankId: number,
    accountNumber?: string,
    unitaryAccountNumber?: string,
    currency?: string,
    beneficiaryId?: number | null
  }): Promise<IBankDepositRealTimeValidationResponseEntity | null> {
    if (!this.isSmartRoutedTransaction || !this.supportsRealTimeValidation)
      return null;

    let res: IBankDepositRealTimeValidationResponseEntity;
    try {
      res = await this.validateRealTime({
        validationType: 'FULL',
        countryTo: params.countryTo,
        bankId: params.bankId,
        accountNumber: params.accountNumber,
        unitaryAccountNumber: params.unitaryAccountNumber,
        currency: params.currency,
        providerId: this.bankDepositProvider.correspondentId,
        mapId: this.bankDepositProvider.providerId,
        correspondentId: this.bankDepositProvider.correspondentId,
        correspondentLocId: this.bankDepositProvider.correspondentLocId,
        beneficiaryId: params.beneficiaryId
      });
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Real-time bank account validation failed, continuing without it');
      return null;
    }

    if (!res || res.ValidationSupported === false) {
      LoggerProvider.info(this, 'Real-time bank account validation unavailable for this account, continuing without it');
      return null;
    }

    if (res.IsValid === false) {
      LoggerProvider.info(this, 'Real-time validation rejected the bank account', res.ValidationChecks);
      return res;
    }

    return null;
  }

  static rejectionMessage(res: IBankDepositRealTimeValidationResponseEntity): string | null {
    if (!res)
      return null;
    const failed = (res.ValidationChecks || []).find(check => !check.Passed && !!check.ErrorMessage);
    if (failed)
      return failed.ErrorMessage;
    return res.Result && res.Result.RetMsg ? res.Result.RetMsg : null;
  }

  private recordTransactionRequirements(res: IBankDepositTransactionRequirementsResponseEntity, bankId: number): void {
    if (!res)
      return;

    this.bankDepositSmartRoutingApplied = res.IsSmartRouting === true;
    this.snapshotToken = res.SnapshotToken;
    this.entitySettings = res.EntitySettings || [];
    this.accountFormats = res.AccountFormats || [];
    this.accountFormatLabel = res.AccountFormatLabel;

    const provider = this.bankDepositSmartRoutingApplied && res.Providers ? res.Providers[0] : null;
    if (!provider) {
      if (this.bankDepositSmartRoutingApplied)
        LoggerProvider.error(this, LoggerEvent.ProviderException, 'Bank deposit smart routing response carried no provider');
      else
        LoggerProvider.info(this, 'Bank deposit transaction requirements came from the legacy routing engine');
      return;
    }
    if (res.Providers.length > 1)
      LoggerProvider.info(this, 'Got multiple qualified providers for bank deposit, using the first');

    this.bankDepositProvider = {
      providerId: res.SmartRoutingProviderId !== null && res.SmartRoutingProviderId !== undefined ? res.SmartRoutingProviderId : provider.MapId,
      correspondentId: provider.ProviderId,
      correspondentLocId: provider.ProviderLocationId,
      bankId
    };
  }

  clearResolvedProvider(): void {
    this.bankDepositSmartRoutingApplied = false;
    this.bankDepositProvider = null;
    this.snapshotToken = null;
    this.entitySettings = [];
    this.accountFormats = [];
    this.accountFormatLabel = null;
  }

  logout(): void {
    this.useBankDepositSmartRouting = false;
    this.supportedFeatures = [];
    this.resolvedCorridor = null;
    this.resolvedCorridorKey = null;
    this.clearResolvedProvider();
  }

  private corridorKey(params: IBankDepositIsSmartRoutingEnabledRequestEntity): string {
    if (!params)
      return null;
    return [params.countryTo, params.currencyFrom, params.currencyTo, params.stateFrom, params.stateTo].join('|');
  }
}



// WEBPACK FOOTER //
// ./src/providers/bank-deposit-smart-routing/bank-deposit-smart-routing.ts